import { useState } from 'react';
import { Plus, Trash2, Building2, ChevronLeft, ChevronRight, Check, Save, X } from 'lucide-react';
import { Modal, Button } from '@components/ui';
import { daireTipleri } from '@mocks/daireIlerleme';
import { toast } from 'react-toastify';
import clsx from 'clsx';

const ADIMLAR = [
    { no: 1, label: 'Genel Bilgiler', ikon: '📋' },
    { no: 2, label: 'Blok Tanımlama', ikon: '🏗️' },
    { no: 3, label: 'Daire Tipleri', ikon: '🏠' },
    { no: 4, label: 'Özet & Onay', ikon: '✅' },
];

const defaultBlok = { name: '', katSayisi: '', katBasinaDaire: '', status: 'active' };

export default function InsaatOlusturModal({ isOpen, onClose, editData = null }) {
    const [adim, setAdim] = useState(1);
    const [genel, setGenel] = useState({
        name: editData?.name || '',
        shortName: editData?.shortName || '',
        parcelInfo: editData?.parcelInfo || '',
        address: editData?.address || '',
        city: editData?.city || '',
        district: editData?.district || '',
        startDate: editData?.startDate || '',
        estimatedEndDate: editData?.estimatedEndDate || '',
        estimatedBudget: editData?.estimatedBudget || '',
        landOwnerShare: editData?.landOwnerShare || 40,
        contractorShare: editData?.contractorShare || 60,
    });
    const [bloklar, setBloklar] = useState(editData?.blocks || [{ ...defaultBlok, name: 'A Blok' }]);
    const [blokTipleri, setBlokTipleri] = useState({}); // { blokIndex: ['2+1', '3+1'] }

    const handleGenelChange = (field, value) => {
        setGenel(prev => ({ ...prev, [field]: value }));
        if (field === 'landOwnerShare') {
            setGenel(prev => ({ ...prev, landOwnerShare: Number(value), contractorShare: 100 - Number(value) }));
        }
    };

    const addBlok = () => {
        const harf = String.fromCharCode(65 + bloklar.length); // A, B, C...
        setBloklar(prev => [...prev, { ...defaultBlok, name: `${harf} Blok` }]);
    };

    const removeBlok = (index) => {
        if (bloklar.length <= 1) return;
        setBloklar(prev => prev.filter((_, i) => i !== index));
        setBlokTipleri(prev => {
            const n = { ...prev };
            delete n[index];
            return n;
        });
    };

    const updateBlok = (index, field, value) => {
        setBloklar(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
    };

    const toggleTip = (blokIndex, tip) => {
        setBlokTipleri(prev => {
            const current = prev[blokIndex] || [];
            const exists = current.includes(tip);
            return {
                ...prev,
                [blokIndex]: exists ? current.filter(t => t !== tip) : [...current, tip],
            };
        });
    };

    const handleSubmit = () => {
        toast.success(`${genel.name} inşaatı başarıyla oluşturuldu`);
        onClose();
    };

    const toplamDaire = bloklar.reduce((a, b) => a + (Number(b.katSayisi) * Number(b.katBasinaDaire) || 0), 0);
    const canNext = () => {
        if (adim === 1) return genel.name && genel.city;
        if (adim === 2) return bloklar.every(b => b.name && b.katSayisi && b.katBasinaDaire);
        return true;
    };

    const inputClass = "w-full px-3 py-2.5 rounded-lg border border-border-light bg-bg-primary text-text-primary text-sm focus:outline-none focus:border-primary placeholder:text-text-muted/50";
    const labelClass = "block text-xs font-medium text-text-secondary mb-1.5";

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Yeni İnşaat Oluştur" size="lg">
            {/* Stepper */}
            <div className="flex items-center gap-1 mb-6 px-2">
                {ADIMLAR.map((a, i) => (
                    <div key={a.no} className="flex items-center flex-1">
                        <div className={clsx(
                            'flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium w-full',
                            adim === a.no ? 'bg-primary/10 text-primary' :
                                adim > a.no ? 'bg-success/10 text-success' : 'bg-bg-tertiary text-text-muted'
                        )}>
                            <span>{adim > a.no ? '✓' : a.ikon}</span>
                            <span className="hidden sm:inline">{a.label}</span>
                        </div>
                        {i < ADIMLAR.length - 1 && <div className="w-4 h-px bg-border-light mx-1 shrink-0" />}
                    </div>
                ))}
            </div>

            {/* Content */}
            <div className="min-h-[350px] max-h-[60vh] overflow-y-auto pr-1">
                {/* Adım 1: Genel Bilgiler */}
                {adim === 1 && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Proje Adı *</label>
                                <input className={inputClass} placeholder="Yeşil Park Konutları" value={genel.name} onChange={e => handleGenelChange('name', e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>Kısa Ad</label>
                                <input className={inputClass} placeholder="Yeşil Park" value={genel.shortName} onChange={e => handleGenelChange('shortName', e.target.value)} />
                            </div>
                        </div>
                        <div>
                            <label className={labelClass}>Adres</label>
                            <input className={inputClass} placeholder="Mahalle, Sokak No, İlçe/İl" value={genel.address} onChange={e => handleGenelChange('address', e.target.value)} />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            <div>
                                <label className={labelClass}>İl *</label>
                                <input className={inputClass} placeholder="İstanbul" value={genel.city} onChange={e => handleGenelChange('city', e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>İlçe</label>
                                <input className={inputClass} placeholder="Kadıköy" value={genel.district} onChange={e => handleGenelChange('district', e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>Parsel Bilgisi</label>
                                <input className={inputClass} placeholder="Ada: 1234, Parsel: 5" value={genel.parcelInfo} onChange={e => handleGenelChange('parcelInfo', e.target.value)} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Başlangıç Tarihi</label>
                                <input type="date" className={inputClass} value={genel.startDate} onChange={e => handleGenelChange('startDate', e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>Tahmini Bitiş</label>
                                <input type="date" className={inputClass} value={genel.estimatedEndDate} onChange={e => handleGenelChange('estimatedEndDate', e.target.value)} />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <div>
                                <label className={labelClass}>Tahmini Bütçe (₺)</label>
                                <input type="number" className={inputClass} placeholder="15000000" value={genel.estimatedBudget} onChange={e => handleGenelChange('estimatedBudget', e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>Arsa Sahibi Pay (%)</label>
                                <input type="number" min="0" max="100" className={inputClass} value={genel.landOwnerShare} onChange={e => handleGenelChange('landOwnerShare', e.target.value)} />
                            </div>
                            <div>
                                <label className={labelClass}>Müteahhit Pay (%)</label>
                                <input type="number" className={inputClass} value={genel.contractorShare} disabled />
                            </div>
                        </div>
                    </div>
                )}

                {/* Adım 2: Blok Tanımlama */}
                {adim === 2 && (
                    <div className="space-y-4">
                        <p className="text-sm text-text-muted">Her blok için kat sayısı ve kat başına daire sayısı belirleyin.</p>
                        {bloklar.map((blok, index) => (
                            <div key={index} className="p-4 bg-bg-tertiary/50 rounded-xl border border-border-light space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold text-text-primary flex items-center gap-2">
                                        <Trash2 size={0} />
                                        🏗️ Blok {index + 1}
                                    </span>
                                    {bloklar.length > 1 && (
                                        <button onClick={() => removeBlok(index)} className="text-error hover:text-error/80 p-1">
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-3 gap-3">
                                    <div>
                                        <label className={labelClass}>Blok Adı *</label>
                                        <input className={inputClass} value={blok.name} onChange={e => updateBlok(index, 'name', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Kat Sayısı *</label>
                                        <input type="number" min="1" max="50" className={inputClass} placeholder="4" value={blok.katSayisi} onChange={e => updateBlok(index, 'katSayisi', e.target.value)} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Kat Başı Daire *</label>
                                        <input type="number" min="1" max="20" className={inputClass} placeholder="2" value={blok.katBasinaDaire} onChange={e => updateBlok(index, 'katBasinaDaire', e.target.value)} />
                                    </div>
                                </div>
                                {blok.katSayisi && blok.katBasinaDaire && (
                                    <p className="text-xs text-text-muted">
                                        Toplam: <span className="font-semibold text-text-primary">{Number(blok.katSayisi) * Number(blok.katBasinaDaire)} daire</span>
                                    </p>
                                )}
                            </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={addBlok} icon={<Plus size={16} />} className="w-full">
                            Blok Ekle
                        </Button>
                        {toplamDaire > 0 && (
                            <div className="bg-primary/5 rounded-lg p-3 text-center">
                                <span className="text-sm text-text-muted">Toplam:</span>
                                <span className="text-lg font-bold text-primary ml-2">{toplamDaire} daire</span>
                                <span className="text-sm text-text-muted ml-1">({bloklar.length} blok)</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Adım 3: Daire Tipleri */}
                {adim === 3 && (
                    <div className="space-y-4">
                        <p className="text-sm text-text-muted">Her blok için hangi daire tiplerinin olacağını seçin.</p>
                        {bloklar.map((blok, bIdx) => (
                            <div key={bIdx} className="space-y-2">
                                <h3 className="text-sm font-semibold text-text-primary">🏗️ {blok.name}</h3>
                                <div className="flex flex-wrap gap-2">
                                    {daireTipleri.map(tip => {
                                        const selected = (blokTipleri[bIdx] || []).includes(tip);
                                        return (
                                            <button
                                                key={tip}
                                                onClick={() => toggleTip(bIdx, tip)}
                                                className={clsx(
                                                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-all border',
                                                    selected
                                                        ? 'bg-primary/10 text-primary border-primary/30'
                                                        : 'bg-bg-tertiary text-text-muted border-border-light hover:border-primary/20'
                                                )}
                                            >
                                                {tip}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Adım 4: Özet */}
                {adim === 4 && (
                    <div className="space-y-4">
                        <div className="bg-bg-tertiary/50 rounded-xl p-5 space-y-3 border border-border-light">
                            <h3 className="font-semibold text-text-primary text-lg">{genel.name || 'İsimsiz Proje'}</h3>
                            {genel.address && <p className="text-sm text-text-muted">{genel.address}</p>}
                            <div className="grid grid-cols-2 gap-3 text-sm">
                                <div><span className="text-text-muted">İl:</span> <span className="text-text-primary font-medium">{genel.city}</span></div>
                                <div><span className="text-text-muted">İlçe:</span> <span className="text-text-primary font-medium">{genel.district || '-'}</span></div>
                                {genel.startDate && <div><span className="text-text-muted">Başlangıç:</span> <span className="text-text-primary font-medium">{genel.startDate}</span></div>}
                                {genel.estimatedBudget && <div><span className="text-text-muted">Bütçe:</span> <span className="text-text-primary font-medium">{Number(genel.estimatedBudget).toLocaleString('tr-TR')} ₺</span></div>}
                                <div><span className="text-text-muted">Pay:</span> <span className="text-text-primary font-medium">Arsa %{genel.landOwnerShare} / Müt. %{genel.contractorShare}</span></div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <h4 className="font-semibold text-text-primary">Bloklar</h4>
                            {bloklar.map((blok, idx) => (
                                <div key={idx} className="flex items-center justify-between px-4 py-3 bg-bg-secondary rounded-lg border border-border-light text-sm">
                                    <span className="font-medium text-text-primary">{blok.name}</span>
                                    <div className="flex items-center gap-4 text-text-muted">
                                        <span>{blok.katSayisi} kat</span>
                                        <span>{Number(blok.katSayisi) * Number(blok.katBasinaDaire)} daire</span>
                                        <span>{(blokTipleri[idx] || []).join(', ') || 'Tip seçilmedi'}</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="bg-success/5 rounded-lg p-4 text-center border border-success/20">
                            <span className="text-sm text-text-muted">Toplam Daire:</span>
                            <span className="text-2xl font-bold text-success ml-2">{toplamDaire}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border-light">
                <Button
                    variant="ghost"
                    onClick={adim === 1 ? onClose : () => setAdim(prev => prev - 1)}
                    icon={adim === 1 ? <X size={16} /> : <ChevronLeft size={16} />}
                >
                    {adim === 1 ? 'İptal' : 'Geri'}
                </Button>
                {adim < 4 ? (
                    <Button
                        variant="primary"
                        onClick={() => setAdim(prev => prev + 1)}
                        disabled={!canNext()}
                        icon={<ChevronRight size={16} />}
                        iconPosition="right"
                    >
                        Devam
                    </Button>
                ) : (
                    <Button variant="primary" onClick={handleSubmit} icon={<Check size={16} />}>
                        Oluştur
                    </Button>
                )}
            </div>
        </Modal>
    );
}
